"use client"

import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react"
import Image from "next/image"
import { motion, useReducedMotion } from "framer-motion"
import { Search, BadgeEuro, UserPlus, Download, Heart, ShoppingCart, Gift, type LucideIcon } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"
import { GlassCard } from "@/components/glass-card"
import { Link, useRouter } from "@/i18n/navigation"
import { LoginButton } from "@/app/[locale]/(auth)/signin/_components/login-button"
import { useLocale, useTranslations } from "next-intl"
import { useShopsWithAssociations } from "@/app/admin/shops/_api/use-shop-queries"
import { useProjects } from "@/app/[locale]/projects/_api/use-project-queries"
import { CommissionDisplay } from "@/app/[locale]/shops/_components/commission-display"
import type { CommissionSummaryType } from "@/openapi/generated"
import { useBrowser, useDevice } from "@/hooks/use-browser"
import { useIsMobile } from "@/hooks/use-mobile"
import { resolveInstallTarget, type Browser } from "@/lib/install-targets"
import { formatCurrencyCompact, formatNumber } from "@/lib/utils/currency"
import { BUTTON_STYLES, ICON_BUTTON_STYLES, TEXT_STYLES, CIRCLE_STYLES, SEARCHBAR_STYLES, paletteColor, tint } from "./hero-styles"
import { type HeroProductView } from "./use-hero-products"
import { DonationCalculator } from "./donation-calculator"

type Locale = "de" | "en"

const fadeUp = (delay: number) => ({
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, delay, ease: "easeOut" as const },
})

/* ----------------------------------------------------------- primitives ---- */

function DonationBadge({ amount, locale }: { amount: number; locale: string }) {
  const t = useTranslations("start.hero")
  return (
    <span className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-primary px-2 py-1.5 text-sm font-bold text-primary-foreground">
      <BadgeEuro className="h-4 w-4 shrink-0" />
      {/* "bis zu" — the amount is an upper bound (shop-max commission), never an exact per-product sum. */}
      {t("upTo")} {formatNumber(amount, locale)} {t("donation")}
    </span>
  )
}

function ProductMedia({ product, className }: { product: HeroProductView; className?: string }) {
  if (product.imageUrl) {
    return (
      <div className={`relative overflow-hidden rounded-xl bg-white ${className ?? "h-28"}`}>
        <Image src={product.imageUrl} alt={product.name} fill className="object-contain p-2" sizes="240px" />
      </div>
    )
  }
  const Icon = product.icon
  return (
    <div className={`flex items-center justify-center rounded-xl bg-gradient-to-br ${product.accent} ${className ?? "h-28"}`}>
      {Icon ? <Icon className="h-12 w-12 text-foreground/40" aria-hidden="true" /> : null}
    </div>
  )
}

/** Product card visual — shared by the hero card-stack and the product-range Produkte tab. */
export function ProductTile({ product, locale, className }: { product: HeroProductView; locale: string; className?: string }) {
  return (
    <GlassCard className={cn("flex flex-col", className)}>
      <ProductMedia product={product} className="h-44 shrink-0" />
      <p className="mt-4 truncate text-lg font-semibold">{product.name}</p>
      <p className="truncate text-xs text-muted-foreground">{product.shopName}</p>
      <p className="mt-1 text-sm font-medium">{formatCurrencyCompact(product.price, locale)}</p>
      {/* `mt-auto` pins the badge to the bottom when the card has a fixed height (showcase stacks);
          with content-driven height (hero swipe / tabs grid) it collapses, keeping the 6px gap. */}
      <div className="mt-auto pt-1.5">
        <DonationBadge amount={product.donation} locale={locale} />
      </div>
    </GlassCard>
  )
}

/* --------------------------------------------------------- card stack ---- */

export function CardStackAuto<T>({
  items,
  renderCard,
  className,
  stackClassName,
  horizontal = false,
  xSpread = 120,
  tilt = 2,
}: {
  items: T[]
  renderCard: (item: T, isFront: boolean) => ReactNode
  className?: string
  stackClassName?: string
  horizontal?: boolean
  xSpread?: number
  tilt?: number
}) {
  const reduce = useReducedMotion()
  const [index, setIndex] = useState(0)
  const hovered = useRef(false)
  useEffect(() => {
    if (reduce || items.length <= 1) return
    const id = setInterval(() => {
      if (!hovered.current) setIndex((p) => (p + 1) % items.length)
    }, 5000)
    return () => clearInterval(id)
  }, [reduce, items.length])

  if (items.length === 0) return null
  const depth = Math.min(3, items.length)
  const stack = Array.from({ length: depth }, (_, o) => items[(index + o) % items.length]!)

  return (
    <div
      className={className ?? "mx-auto w-full max-w-xs"}
      onMouseEnter={() => { hovered.current = true }}
      onMouseLeave={() => { hovered.current = false }}
    >
      <div className={stackClassName ?? (horizontal ? "relative min-h-[300px] overflow-visible" : "relative h-[400px]")}>
        {stack.map((item, si) => {
          const baseScale = 1 - si * 0.05
          return (
            <motion.div
              key={`${index}-${si}`}
              className="absolute inset-x-0"
              style={{ zIndex: 3 - si }}
              animate={horizontal
                ? { x: si === 0 ? 0 : si === 1 ? -xSpread : xSpread, scale: baseScale, opacity: 1 - si * 0.3, rotate: si === 0 ? 0 : si === 1 ? -tilt : tilt }
                : { y: si * 14, scale: baseScale, opacity: 1 - si * 0.3, rotate: si === 0 ? 0 : si === 1 ? tilt : -tilt }
              }
              whileHover={horizontal ? { scale: baseScale * 1.05 } : undefined}
              transition={{ type: "spring", stiffness: 200, damping: 22 }}
            >
              {renderCard(item, si === 0)}
            </motion.div>
          )
        })}
      </div>
      <div className="mt-4 flex justify-center gap-1.5">
        {items.map((_, idx) => (
          <span key={idx} className={`h-1.5 rounded-full transition-all ${idx === index % items.length ? "w-6 bg-primary" : "w-1.5 bg-primary/30"}`} />
        ))}
      </div>
    </div>
  )
}

/* ----------------------------------------------------- shop rate badge ---- */

/** "Bis zu X % Spende" badge — reuses CommissionDisplay for the i18n-correct rate text. */
export function ShopRateBadge({ summary, shopId, block }: { summary?: { max?: number; type?: CommissionSummaryType }; shopId: string; locale: string; block?: boolean }) {
  // `CommissionDisplay` already renders the full "Bis zu X % Spende" string, so we must NOT
  // append another "Spende" here (that produced the duplicate "… Spende Spende").
  if (!summary?.max || summary.max <= 0) return null
  // `block` → full-width centered badge matching the Produkte card's DonationBadge (showcase stacks);
  // default → the compact inline pill used by the rails / steps band.
  if (block) {
    return (
      <span className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-primary px-2 py-1.5 text-sm font-bold text-primary-foreground">
        <CommissionDisplay commissionRangeSummary={summary} shopId={shopId} className="inline" />
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-primary px-2.5 py-1 text-xs font-bold text-primary-foreground">
      <CommissionDisplay commissionRangeSummary={summary} shopId={shopId} className="inline" />
    </span>
  )
}

/* --------------------------------------------------------- hero band ---- */

interface StepDef {
  title: string
  sub: string
  short: string
  icon: LucideIcon
  href?: string
  loginPopup?: boolean
}

/** The 5 steps' code-side meta (icon + link); titles/subs/shorts come from `start.hero.steps.<key>`. */
const STEP_META: { key: string; icon: LucideIcon; href?: string; loginPopup?: boolean }[] = [
  { key: "install", icon: Download },
  { key: "signup", icon: UserPlus, loginPopup: true },
  { key: "cause", icon: Heart, href: "/projects" },
  { key: "shop", icon: ShoppingCart, href: "/shops" },
  { key: "donate", icon: Gift, href: "/impact" },
]

interface ResolvedStep extends StepDef {
  resolvedHref: string
  isExternal: boolean
}

function useStepLinks(): ResolvedStep[] {
  const t = useTranslations("start.hero")
  const device = useDevice()
  const browser = useBrowser()
  return useMemo(() => {
    const installTarget = resolveInstallTarget({ device, browser: browser as Browser })
    return STEP_META.map((m) => {
      const base: StepDef = {
        title: t(`steps.${m.key}.title`),
        sub: t(`steps.${m.key}.sub`),
        short: t(`steps.${m.key}.short`),
        icon: m.icon,
        href: m.href,
        loginPopup: m.loginPopup,
      }
      if (m.loginPopup) return { ...base, resolvedHref: "", isExternal: false }
      if (m.href) return { ...base, resolvedHref: m.href, isExternal: false }
      return { ...base, resolvedHref: installTarget.url, isExternal: true }
    })
  }, [t, device, browser])
}

function StepLink({ step, children, className }: { step: ResolvedStep; children: ReactNode; className?: string }) {
  if (step.loginPopup) {
    // LoginButton renders a shadcn Button whose base adds `[&_svg]:size-4` (shrinks the icon vs the
    // h-6 w-6 of the <a>/<Link> siblings) and whose default variant adds `hover:bg-primary/90` (the
    // circle darkens on hover, unlike the siblings). Neutralize both so all five step icons match.
    return (
      <LoginButton className={cn(className, "hover:bg-primary [&_svg]:!size-6")}>{children}</LoginButton>
    )
  }
  if (step.isExternal) {
    return <a href={step.resolvedHref} target="_blank" rel="noopener noreferrer" className={className}>{children}</a>
  }
  return <Link href={step.resolvedHref} className={className}>{children}</Link>
}

/** stepsPath — icon nodes on a single connector line drawn on mount (5-step). */
function HeroStepsPath({ circleClass }: { circleClass?: string }) {
  const reduce = useReducedMotion()
  const steps = useStepLinks()
  return (
    <div className="relative mx-auto max-w-5xl py-8 md:py-12">
      {/* connector line (desktop only — drops to a plain grid on mobile) */}
      <motion.div
        className="absolute left-[10%] right-[10%] top-[4.75rem] hidden h-0.5 origin-left bg-primary/30 md:block"
        initial={reduce ? false : { scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      />
      <div className="grid grid-cols-1 gap-y-8 md:grid-cols-5 md:gap-x-4 md:gap-y-10">
        {steps.map((step, i) => {
          const Icon = step.icon
          return (
            <motion.div
              key={i}
              className="relative flex flex-col items-center text-center"
              initial={reduce ? false : { opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.1, ease: "easeOut" }}
            >
              <StepLink step={step} className={cn("z-10 flex h-14 w-14 cursor-pointer items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 transition-all hover:scale-105 hover:shadow-xl hover:shadow-primary/40", circleClass)}>
                <Icon className="h-6 w-6" />
              </StepLink>
              <p className="mt-3 text-sm font-semibold leading-tight">{step.title}</p>
              <p className="mt-1 text-xs text-muted-foreground">{step.sub}</p>
            </motion.div>
          )
        })}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------- search ---- */

function HeroSearch({ placeholder, submitStyle, submitColor, barStyle }: { placeholder: string; submitStyle?: string; submitColor?: string; barStyle?: string }) {
  const router = useRouter()
  const [term, setTerm] = useState("")
  function onSubmit(e: FormEvent) {
    e.preventDefault()
    const q = term.trim()
    if (q) router.push(`/search?q=${encodeURIComponent(q)}`)
  }
  return (
    <form onSubmit={onSubmit} className="w-full md:flex-1">
      {/* tint the wrapper so the input focus ring (--ring) + submit button (--primary) recolor together */}
      <div className="relative" style={tint(submitColor)}>
        <Input value={term} onChange={(e) => setTerm(e.target.value)} placeholder={placeholder} className={cn("h-12 bg-white/65 pr-12", barStyle ?? "rounded-xl")} />
        <Button type="submit" size="icon" className={cn("absolute right-1.5 top-1/2 h-9 w-9 -translate-y-1/2 rounded-lg", submitStyle)}>
          <Search className="h-4 w-4" />
        </Button>
      </div>
    </form>
  )
}

/* ----------------------------------------------------------- composite ---- */

export function HeroComposite() {
  const locale = (useLocale() === "de" ? "de" : "en") as Locale
  const t = useTranslations("start.hero")

  // Locked Design Lab choices (formerly per-element style toggles). All accent
  // colors are theme green except the 5-step icons (logo red), so only those get a tint.
  const headlineStyle = TEXT_STYLES.highlightSolid!
  // CTA: on mobile (no hover) force solid green so the `outlineFill` default isn't a weak
  // outline; desktop (≥768px, hover exists) keeps the chosen outline → fill on hover.
  const isMobile = useIsMobile()
  const ctaStyle = isMobile ? BUTTON_STYLES.solidShadow! : BUTTON_STYLES.outlineFill!
  const searchStyle = ICON_BUTTON_STYLES.solid!
  const searchBarStyle = SEARCHBAR_STYLES.default!
  const stepsStyle = CIRCLE_STYLES.ping!
  const stepsColor = paletteColor("logoRed")

  // Live counts for the {shops}/{charities} tokens — reuse the queries already
  // prefetched by this page (matching params) so there's no fetch/flicker.
  const { data: shopsData } = useShopsWithAssociations({ size: 1, page: 1, monetized: true })
  const { data: charitiesData } = useProjects({ size: 20, page: 1 })
  const shopCount = shopsData?.totalElements ?? 0
  const charityCount = charitiesData?.totalElements ?? 0

  const shops = shopCount > 0 ? formatNumber(shopCount, locale) : locale === "de" ? "tausenden" : "thousands"
  const charities = charityCount > 0 ? formatNumber(charityCount, locale) : locale === "de" ? "vielen" : "many"

  return (
    <section className="relative flex items-center overflow-hidden py-12 lg:min-h-[80vh] lg:py-20">
      <div className="relative z-10 mx-auto w-full max-w-7xl px-4">
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-8">
          {/* Left — text */}
          <div className="max-w-xl text-left">
            <motion.div {...fadeUp(0)}>
              <h1 className="text-balance text-4xl font-bold leading-[1.2] tracking-normal sm:text-5xl md:text-6xl">
                {t.rich("headline", { green: (chunks) => <span className={headlineStyle}>{chunks}</span> })}
              </h1>
            </motion.div>

            <motion.div {...fadeUp(0.2)}>
              <p className="mt-6 max-w-xl text-pretty text-lg leading-relaxed text-muted-foreground md:text-xl">
                {t("subtitle", { shops, charities })}
              </p>
            </motion.div>

            <motion.div {...fadeUp(0.3)}>
              <div className="mt-10 flex flex-col items-start gap-4 md:flex-row md:items-center">
                <LoginButton size="lg" className={cn("h-12 w-full shrink-0 rounded-xl px-8 text-base md:w-auto", ctaStyle)}>
                  {t("primary")}
                </LoginButton>
                <HeroSearch placeholder={t("searchPlaceholder")} submitStyle={searchStyle} barStyle={searchBarStyle} />
              </div>
            </motion.div>
          </div>

          {/* Right — donation calculator */}
          <motion.div {...fadeUp(0.25)}>
            <DonationCalculator locale={locale} />
          </motion.div>
        </div>

        {/* Hero band — 5-step connector path */}
        <motion.div {...fadeUp(0.45)} className="mt-16 space-y-4 lg:mt-20" style={tint(stepsColor)}>
          <HeroStepsPath circleClass={stepsStyle} />
        </motion.div>
      </div>
    </section>
  )
}
